import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ListsService } from './lists.service';
import { TargetingRule } from '../uas/editing/targeting/targeting-rule.interface';

type RuleType = 'time_rule' | 'list_rule' | 'is_is_not_list' | 'one_off_rule';

interface RuleDisplayContext {
  partnerId?: number;
  startsOn?: string;
  endsOn?: string;
  subfleetMap?: {[id: string]: string};
  targetNameFormat?: 'html' | 'text';
  onDisplayDataUpdated?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class TargetingRuleDisplayService {
  private readonly listOptionTypeIds = new Set([
    'airport_list',
    'country_list',
    'equipment_class_list',
    'origin_and_destination_list',
    'subfleet_list',
    'terminal_list',
    'tail_list',
    'stopover_connection_list',
  ]);

  private readonly amenityListOptionTypeIds = new Set([
    'beverage_list',
    'entertainment_list',
    'food_list',
    'layout_list',
    'power_list',
    'seat_list',
    'wifi_list',
  ]);

  private readonly amenityOptionTypeIds = new Set([
    'equipment_class',
    'beverage',
    'entertainment',
    'food',
    'layout',
    'power',
    'seat',
    'wifi',
  ]);

  private readonly timeTemplateIds = new Set([
    'departure-time',
    'arrival-time',
    'elapsed-time',
    'flight-date',
    'arrival-layover-duration',
    'display-dates',
  ]);

  constructor(
    private listsService: ListsService,
    private sanitizer: DomSanitizer,
  ) {}

  isListRule(rule: TargetingRule): boolean {
    const id = rule?.option_type_id;
    return id?.indexOf('_list') > -1 && id !== 'position_list';
  }

  getRuleType(rule: TargetingRule): RuleType {
    if (this.isTimeTemplate(rule.template_id)) {
      return 'time_rule';
    }

    if (this.isListRule(rule)) {
      return 'list_rule';
    }

    if (this.amenityOptionTypeIds.has(rule.option_type_id)) {
      return 'is_is_not_list';
    }

    return 'one_off_rule';
  }

  getRuleTargetName(rule: TargetingRule, context: RuleDisplayContext = {}): string | ReturnType<DomSanitizer['bypassSecurityTrustHtml']> | undefined {
    if (rule.option_type_id?.indexOf('_list') > -1) {
      return rule.list_name;
    }

    switch (rule.option_type_id) {
      case 'airport':
      case 'stopover_connection':
        return rule.airport_code
      case 'terminal':
        return rule.terminal_code
      case 'country':
        return rule.country_code
      case 'subfleet':
        return rule.subfleet_id !== undefined ? context.subfleetMap?.[rule.subfleet_id] : undefined;
      case 'origin_and_destination':
        if (context.targetNameFormat === 'text') {
          return `${rule.from} and ${rule.to}`;
        }
        const bothWays = rule.both_ways
          ? '<svg class="icon icon-arrow-right-arrow-left" style="height: 16px"><use xlink:href="#icon-arrow-right-arrow-left"></use></svg>'
          : '';
        return this.sanitizer.bypassSecurityTrustHtml(
          `<span style="display: inline-flex; align-items: center; gap: 6px;">${rule.from} and ${rule.to} ${bothWays}</span>`,
        );
      case 'carriers':
        return rule.carrier_codes?.join(', ');
      case 'equipment_class':
        return rule.equipment_class_value;
      case 'entertainment':
      case 'layout':
      case 'power':
      case 'seat':
      case 'beverage':
      case 'food':
      case 'wifi':
        return rule.display_text_name;
      case 'date_interval':
        return `${rule.start} and ${rule.stop}`;
      case 'duration_interval':
      case 'stopover_duration_interval':
        return this.getDurationRangeDisplay(rule);
      case 'time_interval':
        return `${rule.start?.hour}:${rule.start?.minute} - ${rule.stop?.hour}:${rule.stop?.minute}`;
      case 'time':
        if (rule.operator_id === 'is-between') {
          return `${rule.start?.hour}:${rule.start?.minute} - ${rule.stop?.hour}:${rule.stop?.minute}`;
        }
        return `${rule.value?.hour}:${rule.value?.minute}`;
      default:
        return rule.target_name;
    }
  }

  populateRuleDisplayData(rule: TargetingRule, context: RuleDisplayContext = {}): void {
    if (this.isTimeTemplate(rule.template_id)) {
      rule.list_name = this.getTimeRuleLabel(rule.template_id);
      rule.time_details = this.getTimeRuleDetails(rule, context);
      return;
    }

    if (this.listOptionTypeIds.has(rule.option_type_id)) {
      this.loadStandardListDisplayData(rule, context);
      return;
    }

    if (rule.option_type_id === 'position_list') {
      this.loadPositionListDisplayData(rule, context);
      return;
    }

    if (this.amenityListOptionTypeIds.has(rule.option_type_id)) {
      this.loadAmenityListDisplayData(rule, context);
      return;
    }

    rule.target_name = this.getRuleTargetName(rule, context);
  }

  private isTimeTemplate(templateId: string): boolean {
    return this.timeTemplateIds.has(templateId);
  }

  private getTimeRuleLabel(templateId: string): string {
    const labels: Record<string, string> = {
      'departure-time': 'Time',
      'arrival-time': 'Time',
      'elapsed-time': 'Duration',
      'flight-date': 'Departure date',
      'arrival-layover-duration': 'Layover duration',
      'display-dates': 'Dates',
    };
    return labels[templateId] ?? '';
  }

  private getTimeRuleDetails(rule: TargetingRule, context: RuleDisplayContext): string {
    switch (rule.template_id) {
      case 'departure-time':
      case 'arrival-time':
        if (rule.operator_id === 'is-between') {
          return `${rule.start?.hour}:${rule.start?.minute} - ${rule.stop?.hour}:${rule.stop?.minute}`;
        }
        return `${rule.value?.hour}:${rule.value?.minute}`;
      case 'arrival-layover-duration':
      case 'elapsed-time':
        return this.getDurationRangeDisplay(rule);
      case 'flight-date':
        return `${formatDate(rule.start, 'MMM d, YYYY', 'en-US')} - ${formatDate(rule.stop, 'MMM d, YYYY', 'en-US')}`;
      case 'display-dates':
        return `${context.startsOn ? formatDate(context.startsOn, 'MMM d, YYYY', 'en-US') : 'Today'} - ${context.endsOn ? formatDate(context.endsOn, 'MMM d, YYYY', 'en-US') : 'Forever'}`;
      default:
        return '';
    }
  }

  private loadStandardListDisplayData(rule: TargetingRule, context: RuleDisplayContext): void {
    // No partner to scope the request to — same pre-existing no-op the code fell into
    // before (an unscoped call would have hit `/airlines/undefined/..._lists`).
    if (context.partnerId === undefined) {
      return;
    }
    const optionType = rule.option_type_id === 'stopover_connection_list'
      ? 'airport'
      : rule.option_type_id.substring(0, rule.option_type_id.length - 5);

    this.listsService.getList(context.partnerId, optionType, rule.option_id).subscribe((list: { name: string }) => {
      this.setBasicListDisplayData(rule, list.name, context);
    });
  }

  private loadPositionListDisplayData(rule: TargetingRule, context: RuleDisplayContext): void {
    this.listsService.getStaticList('positions')?.subscribe((list: Array<{ id: unknown; name: string }>) => {
      const listName = list.find(item => item.id === rule.option_id)?.name;
      this.setBasicListDisplayData(rule, listName, context);
    });
  }

  private loadAmenityListDisplayData(rule: TargetingRule, context: RuleDisplayContext): void {
    // Same no-partner no-op as loadStandardListDisplayData above.
    if (context.partnerId === undefined) {
      return;
    }
    const optionType = this.toAmenityListKey(rule.option_type_id);

    this.listsService.getLists(context.partnerId, 'amenity').subscribe((amenityLists: Record<string, Array<{ id: unknown; name: string; display_texts: Array<{ display_text_name: string }> }>>) => {
      const matchingList = amenityLists[optionType]?.find(list => list.id === rule.option_id);
      if (!matchingList) {
        return;
      }

      this.setBasicListDisplayData(rule, matchingList.name, context);
      rule.rule_content = matchingList.display_texts.map((displayText) => ({ name: displayText.display_text_name }));
      context.onDisplayDataUpdated?.();
    });
  }

  private setBasicListDisplayData(rule: TargetingRule, listName: string | undefined, context: RuleDisplayContext): void {
    rule.list_name = listName;
    rule.active = false;
    rule.target_name = listName;
    context.onDisplayDataUpdated?.();
  }

  private toAmenityListKey(optionTypeId: string): string {
    const optionType = optionTypeId.substring(0, optionTypeId.length - 5);
    return optionType.charAt(0).toUpperCase() + optionType.slice(1);
  }

  private getDurationRangeDisplay(rule: TargetingRule): string {
    const toTime = (time?: { hour?: string | number; minute?: string | number }) => `${time?.hour ?? 0}h ${time?.minute ?? 0}m`;

    const stopIsForever = !rule.stop || (rule.stop.hour ?? 0) === 0 && (rule.stop.minute ?? 0) === 0;

    return `${toTime(rule.start)} - ${stopIsForever ? 'Forever' : toTime(rule.stop)}`;
  }
}
